import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Box as Wrapper,
  DarkMode,
  Flex,
  Icon,
  Image,
  LightMode,
  Space,
  Stack,
  Text,
  Link,
} from '@tonic-ui/react';
import { css, Global } from '@emotion/react';
import logo from './assets/images/ref-by-code/logo.svg';
import { isSessionValid, logoutSession } from './ds-api';
import {
  checkGovCloudRedirect,
  getCurrentService,
  getNonServiceRoute,
  isGovCloudRegion,
  isServiceEnabledForTargetEnv,
  getV1PortalURL,
  navigateToV1ES,
} from './utils';
import { withTranslation } from 'react-i18next';
import {
  getAccountsList,
  getCommonType,
  getServices,
  getUserDetails,
  getAccountServicesMigratedState,
  getToken,
} from './store';
import { Banner, DropdownMenu, LangToggle, ServiceDropdownMenu } from './components';
import { supportedLocales } from './locales/supportedLocales';
import GlobalLoader from './components/loader/global-loader';
import NotificationsDropdownMenu from './components/notifications-center';

function navigateApp(appName) {
  return {
    type: 'NAVIGATE_APP',
    appName,
  };
}

export const hasEnableMfeTemplate = JSON.parse(localStorage.getItem('C1C_MFE_TEMPLATE') || 'false');

class App extends Component {
  initialState = {
    value: localStorage.getItem(`${process.env.APP_PREFIX}selected_service`),
    showDropDown: false,
    isLoggedIn: false,
    hasSSOv2: false,
    showBanner: false,
    isSAMLUser: false,
    selectedAccount: '',
    userAccounts: [],
    cloudOneAccounts: [],
    supportLinks: [],
  };
  state = this.initialState;

  window = (window.c1 = {});
  currentPath = window.location.pathname;

  CUSTOM_PATHS = {
    SIGN_IN: 'signin',
  };
  supportLinks = () => [
    {
      label: this.props.t('helpDropdown.apiGuide'),
      value: 'docs',
      disabled: false,
    },
    {
      label: this.props.t('helpDropdown.support'),
      value: 'support',
      disabled: false,
    },
  ];

  navigateToPath = (path) => {
    const { SIGN_IN } = this.CUSTOM_PATHS;
    const paths = [SIGN_IN];
    const matchPath = paths.find((id) => id === path);

    if (matchPath === SIGN_IN) {
      console.warn('Signin request redirect from Workload');
      return window.history.pushState({}, '', '/');
    }
  };

  determineBannerVisibility = () => {
    const routesWithBanner = isGovCloudRegion ? ['/home'] : [];
    const nextShowBanner = routesWithBanner.includes(window.location.pathname);

    if (this.currentPath === window.location.pathname && this.state.showBanner === nextShowBanner) {
      return;
    }

    this.currentPath = window.location.pathname;
    if (this.state.showBanner !== nextShowBanner) {
      this.setState({ showBanner: nextShowBanner });
    }
  };

  /**
   * Get Matched Service by pathname
   * return /pathname
   */
  getCurrentServiceByPath = (routes = []) =>
    routes.find((route) => window.location.pathname === `/management${route}`);

  /**
   * Management Route Redirect Handler
   */
  checkRouteRedirects = () => {
    if (window.location.pathname.startsWith('/management')) {
      const currentURL = new URL(window.location.href);
      const myUserRoutes = ['/my-profile', '/invitations', '/accounts'];
      const integrationRoutes = [
        '/cloudprovideraccounts',
        '/developer-integrations',
        '/outbound-integrations',
        '/vision-one',
      ];
      const pathnameURL = currentURL.pathname.split('/management')[1];
      const matchedUserRoute = this.getCurrentServiceByPath(myUserRoutes);

      if (matchedUserRoute) {
        return this.props.globalEventDistributor.dispatch(navigateApp(`my-user${pathnameURL}`));
      }

      const matchedIntegrationRoute = this.getCurrentServiceByPath(integrationRoutes);
      if (matchedIntegrationRoute) {
        return this.props.globalEventDistributor.dispatch(
          navigateApp(`integrations${pathnameURL}`)
        );
      }

      if (window.location.pathname === '/management/') {
        return this.props.globalEventDistributor.dispatch(
          navigateApp(`administration/account-properties`)
        );
      }

      return this.props.globalEventDistributor.dispatch(
        navigateApp(`administration${pathnameURL}`)
      );
    }
  };

  stateMachine = (isLoggedIn) => {
    // Set the current service to 'administration', 'my-user' or 'integrations' when SSOv2 is enabled
    // If the user in non-V2 user and the current service === management, don't' show it.
    const currentService = getCurrentService(window.location.pathname);

    const getCurrentSelectedService = () =>
      localStorage.getItem(`${process.env.APP_PREFIX}selected_service`);

    const hasLastAccessedAccount =
      location.pathname.startsWith('/create-account') &&
      localStorage.getItem('lastAccessedAccountID') !== '';

    if (isLoggedIn && currentService && isGovCloudRegion) {
      // Redirect all security services to home (except WS) in GovCloud
      checkGovCloudRedirect(window.location.pathname);
    }

    if (isLoggedIn && !currentService) {
      // if user has selected an account, hide setting the banner for create-account page
      if (hasLastAccessedAccount) {
        return this.setState({
          isLoggedIn: true,
          isSAMLUser: this.props.authType === 'saml',
          showDropDown: false,
        });
      }
      this.setState(
        {
          isLoggedIn: true,
          isSAMLUser: this.props.authType === 'saml',
          showDropDown: false,
        },
        () => {
          if (!this.state.hasSSOv2 && !window.location.pathname.startsWith('/home')) {
            // When getting to workload, it will load starting with a hash, single-spa navigate does not register the change,
            // therefore updating the url solves this use-case and works for all other routes
            return window.history.pushState({}, '', '/home');
          }
          if (!getNonServiceRoute(window.location.pathname)) {
            return window.history.pushState({}, '', '/home');
          }
        }
      );
    } else if (isLoggedIn && currentService) {
      // Check for /management routes -> redirect to new root paths
      this.checkRouteRedirects();

      const topLevelManagementRoutes = ['/administration', '/integrations', '/my-user'];
      const currentPathname =
        '/' + window.location.pathname.split('/')[1] !== currentService
          ? currentService
          : window.location.pathname;
      const matchedtopLevelManagementRoute = topLevelManagementRoutes.find(
        (route) => currentPathname === route || currentPathname === `${route}/`
      );

      if (matchedtopLevelManagementRoute === '/my-user') {
        return this.props.globalEventDistributor.dispatch(navigateApp('my-user/my-profile'));
      } else if (matchedtopLevelManagementRoute === '/integrations') {
        return this.props.globalEventDistributor.dispatch(
          navigateApp('integrations/cloudprovideraccounts')
        );
      } else if (matchedtopLevelManagementRoute === '/administration') {
        return this.props.globalEventDistributor.dispatch(
          navigateApp('administration/account-properties')
        );
      }

      if (this.state.hasSSOv2 && this.props.c1AccountsList.length < 1) {
        return this.setState({
          isLoggedIn: true,
          isSAMLUser: this.props.authType === 'saml',
          selectedAccount: this.props.t('accountsDropdown.selectAccount'),
          showDropDown: false,
        });
      }

      // Hide the Service Selection Dropdown for web-app-support and other services when there is no lastAccessedAccountID
      let valueForShowDropDown;
      if (this.state.hasSSOv2) {
        valueForShowDropDown =
          localStorage.getItem('lastAccessedAccountID') !== null && currentService !== '/support';
      } else {
        valueForShowDropDown =
          currentService !== '/support' && currentService !== '/update-account';
      }

      this.setState({
        isLoggedIn: true,
        isSAMLUser: this.props.authType === 'saml',
        value: getCurrentSelectedService(),
        showDropDown: valueForShowDropDown,
      });
    } else {
      this.setState(
        {
          isLoggedIn: false,
          isSAMLUser: false,
          value: getCurrentSelectedService(),
          showDropDown: false,
        },
        () => {
          // Added this condition for not executing this callback when signing out (/)
          if (window.location.pathname !== '/') return;
          this.props.globalEventDistributor.dispatch(navigateApp(''));
        }
      );
    }
  };

  // For SSO v2 Only
  updateAccountsMenu = (c1AccountsList = []) => {
    const { t } = this.props;
    const lastSelectedAccountID = () => localStorage.getItem('lastAccessedAccountID') || '';
    const formattedAccountsList = c1AccountsList.map(({ id, alias, state, region }) => {
      // TODO Temporary change - to be removed once alias value is available
      if (alias === undefined) {
        alias = t('accountsDropdown.accountAliasMissing');
      }
      return {
        label: `${alias} - ${id}`,
        value: id,
        state,
        disabled: false,
        region,
        alias: 'accountAliasMissing', // used to trigger locale change
      };
    });

    if (formattedAccountsList && formattedAccountsList.length === 0) {
      this.setState((prevState) => ({
        ...prevState,
        cloudOneAccounts: [
          {
            label: 'noJoinedAccounts',
            value: 'noJoinedAccounts',
            disabled: false,
          },
        ],
        selectedAccount: {
          label: t('accountsDropdown.selectAccount'),
          value: '',
          disabled: false,
        },
      }));
    } else if (lastSelectedAccountID() === '') {
      this.setState((prevState) => ({
        ...prevState,
        cloudOneAccounts: [
          {
            label: 'noLastAccessedAccount',
            value: 'noLastAccessedAccount',
            disabled: false,
          },
        ],
        selectedAccount: {
          label: t('accountsDropdown.selectAccount'),
          value: '',
          disabled: false,
        },
      }));
    } else {
      // check if account exist in AccountsList
      const accountDetails =
        this.state.selectedAccount.value !== lastSelectedAccountID()
          ? formattedAccountsList.find((account) => account.value === lastSelectedAccountID())
          : formattedAccountsList.find(
              (account) => account.value === this.state.selectedAccount.value
            );
      this.setState((prevState) => ({
        ...prevState,
        cloudOneAccounts: accountDetails
          ? [...formattedAccountsList]
          : [
              {
                label: 'noLastAccessedAccount',
                value: 'noLastAccessedAccount',
                disabled: false,
              },
            ],
        selectedAccount: accountDetails ?? t('accountsDropdown.selectAccount'),
      }));
    }
  };

  componentDidMount() {
    const hasRID = window.sessionStorage.getItem('rID') || false;
    const hasSSOv2Enabled = localStorage.getItem('C1C_SSO_V2_ENABLED')
      ? JSON.parse(localStorage.getItem('C1C_SSO_V2_ENABLED'))
      : false;
    // for workload security callback
    if (!hasSSOv2Enabled) {
      window.c1.cb = (isWorkloadLoggedIn) => this.stateMachine(isWorkloadLoggedIn);
    }
    // for non-workload services
    !hasSSOv2Enabled && hasRID
      ? isSessionValid().then((isLoggedIn) => this.stateMachine(isLoggedIn))
      : hasSSOv2Enabled
      ? this.setState({ hasSSOv2: hasSSOv2Enabled }, () =>
          this.stateMachine(this.props.loggedInSSOv2)
        )
      : this.stateMachine(hasRID);

    const accountsStorage = sessionStorage.getItem('C1C_Shell_State') || '{}';
    const accountsList = accountsStorage
      ? JSON.parse(accountsStorage)?.c1Accounts?.c1AccountsList
      : [];

    if (hasSSOv2Enabled && accountsList) {
      this.updateAccountsMenu(accountsList);
    }

    this.updateSupportLinks();
    this.determineBannerVisibility();

    this.currentPath = window.location.pathname;
    window.addEventListener('popstate', (evt) => {
      this.determineBannerVisibility();

      if (!evt.singleSpa) {
        let servicePath;

        // Handle the browser back button behaviour for nested /administration pages
        if (
          (hasSSOv2Enabled && window.location.pathname.startsWith('/management')) ||
          window.location.pathname.startsWith('/administration') ||
          window.location.pathname.startsWith('/my-user') ||
          window.location.pathname.startsWith('/integrations')
        ) {
          servicePath = window.location.pathname.slice(1);
        } else {
          // Added this condition to handle back button behaviour for root route
          window.location.pathname === '/' && !this.props.loggedInSSOv2
            ? (servicePath = window.location.pathname)
            : (servicePath = window.location.pathname.split('/').slice(1, 2).join('/'));
        }

        if (servicePath) {
          this.props.globalEventDistributor.dispatch(navigateApp(servicePath));
        }

        if (servicePath === '' && this.props.loggedInSSOv2) {
          this.props.globalEventDistributor.dispatch(navigateApp(servicePath));
        }
      }
    });

    // Multi-Tab Account Switching and Logging out feature
    window.addEventListener('storage', (evt) => {
      if (hasSSOv2Enabled && evt.key === 'C1C_ACCOUNT_SWITCH') {
        if (localStorage.getItem('lastAccessedAccountID') !== null) {
          this.onAccountSelect('accountSwitch');
        }
      }
    });

    // Check if locale is available in localStorage, after header has loaded
    // Required to set locale before user logs in
    const storedLocale = localStorage.getItem(`${process.env.APP_PREFIX}locale`) || false;
    if (storedLocale) {
      const isValidLocale = supportedLocales.includes(storedLocale);
      this.props.globalEventDistributor.dispatch({
        type: 'SET_LOCALE',
        locale: isValidLocale ? storedLocale : 'en',
      });
      if (!isValidLocale) {
        localStorage.setItem(`${process.env.APP_PREFIX}locale`, 'en');
      }
    } else if (hasSSOv2Enabled) {
      const browserLang = navigator.language;
      const locale = browserLang.includes('-') ? browserLang.split('-')[0] : browserLang;
      // Default locale to 'en' for any language other than Japanese
      localStorage.setItem(`${process.env.APP_PREFIX}locale`, locale === 'ja' ? 'ja' : 'en');
      this.props.globalEventDistributor.dispatch({
        type: 'SET_LOCALE',
        locale: locale === 'ja' ? 'ja' : 'en',
      });
    }
    const { theme } = this.props;
    this.props.globalEventDistributor.dispatch({
      type: 'SET_THEME_OVERRIDES',
      overrides: theme.overrides,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      appSelection,
      authType,
      loggedInSSOv2,
      wsLoggedIn,
      c1AccountsList,
      locale,
      i18n,
      singleSpa,
    } = this.props;

    this.determineBannerVisibility();

    const { isLoggedIn, hasSSOv2, selectedAccount } = this.state;

    if (wsLoggedIn && prevProps.wsLoggedIn !== wsLoggedIn && hasSSOv2) {
      this.setState({ hasSSOv2: false }, () => this.stateMachine(wsLoggedIn));
    } else if (!wsLoggedIn && prevProps.wsLoggedIn !== wsLoggedIn) {
      this.setState(
        {
          ...this.initialState,
          supportLinks: this.state.supportLinks,
          hasSSOv2: true,
        },
        () => {
          return singleSpa.navigateToUrl(`/`);
        }
      );
    }

    /**
     * SSO v2 -- Start
     */
    if (hasSSOv2) {
      if (loggedInSSOv2) {
        if (prevProps.c1AccountsList !== c1AccountsList) {
          return this.updateAccountsMenu(c1AccountsList);
        }
        if (prevProps.appSelection !== appSelection) {
          return this.stateMachine(loggedInSSOv2);
        }
        if (prevProps.authType !== authType) {
          return this.stateMachine(loggedInSSOv2);
        }
      }
      if (prevProps.loggedInSSOv2 !== loggedInSSOv2) {
        return this.stateMachine(loggedInSSOv2);
      }
    }
    /*** end ***/

    if (prevProps.appSelection !== appSelection) {
      this.stateMachine(isLoggedIn);
    }

    if (
      prevState.isLoggedIn !== isLoggedIn &&
      appSelection &&
      !appSelection.startsWith('workload')
    ) {
      this.stateMachine(isLoggedIn);
    }

    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale);
      // If no selected account or missingAlias call updateAccountsMenu
      // to update locale for `Select Account` or `Account Alias Missing`
      if (!selectedAccount?.value || selectedAccount?.alias?.includes('accountAliasMissing')) {
        this.updateAccountsMenu(c1AccountsList);
      }

      this.updateSupportLinks();
    }
  }

  onServiceSelect = ({ value }) => {
    if (this.props.workloadMigrationState === 'migrated' && value === 'workload') {
      return navigateToV1ES();
    }

    console.log(`Dispatching NAVIGATE_APP from Top Header bar ${value}`);
    this.setState({ value: value, showDropDown: true });
    if (value === 'conformity') {
      value = 'conformity/';
    }

    this.props.globalEventDistributor.dispatch(navigateApp(`${value}`));
  };

  onLogoClick = () => {
    console.log(`Dispatching NAVIGATE_APP from Top Header bar`);
    const { isLoggedIn, hasSSOv2 } = this.state;

    if (hasSSOv2 && isLoggedIn) {
      if (this.props.c1AccountsList.length < 1) return;
    }

    // Rescue WS login state navigation mitigation.
    if (this.props.wsNavigationRestricted) return;

    this.setState({ showDropDown: false });

    if (hasSSOv2 && !isLoggedIn) {
      return this.props.globalEventDistributor.dispatch(navigateApp('/'));
    }

    this.props.globalEventDistributor.dispatch(navigateApp('home'));
  };

  // v1
  onLogout = () => {
    logoutSession().then(() => {
      const { hasSSOv2 } = this.state;
      const { wsLoggedIn, globalEventDistributor, singleSpa } = this.props;

      if (wsLoggedIn) {
        localStorage.setItem('C1C_SSO_V2_ENABLED', true);
        localStorage.removeItem('WS_RESCUE_LOGGED_IN_USER');
        globalEventDistributor.dispatch({ type: 'WS_LOGGED_OUT' });
        this.setState(
          {
            ...this.initialState,
            supportLinks: this.state.supportLinks,
            hasSSOv2: true,
          },
          () => {
            return singleSpa.navigateToUrl(`/`);
          }
        );
      } else {
        this.setState(
          {
            ...this.initialState,
            supportLinks: this.state.supportLinks,
            hasSSOv2,
          },
          () => {
            singleSpa.navigateToUrl(`/signout`);
            setTimeout(() => singleSpa.navigateToUrl(`/`), 0);
          }
        );
      }
    });
  };

  selectFilter = (type, account) => {
    if (account === '') return;

    this.props.globalEventDistributor.dispatch({
      type,
      c1AccountID: account,
    });
  };

  /**
   * Update User selection
   * @param value
   */
  onUserSelect = ({ value }) => {
    if (value === 'sign-out') {
      return this.onSignOut();
    } else if (value === 'my-profile') {
      this.props.globalEventDistributor.dispatch(navigateApp('my-user/my-profile'));
    }
  };

  /**
   * SSO v2 Sign-out state
   */
  onSignOut = () => {
    this.props.globalEventDistributor.dispatch({ type: 'LOGGING_OUT' });
    this.setState({
      ...this.initialState,
      hasSSOv2: this.state.hasSSOv2,
      supportLinks: this.state.supportLinks,
    });
    // Used to trigger clearing the current session when multiple-tabs are open
    localStorage.setItem(`${process.env.APP_PREFIX}CLEAR_SESSION`, Date.now());
  };

  onAccountSelect = (selectedAccount) => {
    const { value: accountID } = selectedAccount;

    // User attempts to select the same account they're currently on
    if (this.state.selectedAccount.value === accountID || accountID === 'noJoinedAccounts') return;

    const currentRoute = (route) => window.location.pathname.startsWith(route);

    if (!currentRoute('/administration/account-properties') && accountID === 'manageAccount') {
      this.props.globalEventDistributor.dispatch(navigateApp('administration/account-properties'));
    } else if (!currentRoute('/create-account') && accountID === 'createAccount') {
      this.props.globalEventDistributor.dispatch(navigateApp('create-account'));
    } else if (!currentRoute('/account-selection') && accountID === 'accountSwitch') {
      this.props.globalEventDistributor.dispatch(navigateApp('account-selection'));
    } else {
      return null;
    }
  };

  onSupportSelect = ({ value }) => {
    if (value === 'docs') {
      return window.open('/docs', '_blank', 'noopener,noreferrer');
    }

    if (value === 'en' || value === 'ja') {
      localStorage.setItem('C1C_locale', value);
      this.props.globalEventDistributor.dispatch({ type: 'SET_LOCALE', locale: value });
    }

    if (value === 'support') {
      if (this.state.isLoggedIn) {
        return window.open('/support', '_blank', 'noopener,noreferrer');
      } else {
        if (this.state.hasSSOv2) {
          if (isGovCloudRegion) {
            return this.props.globalEventDistributor.dispatch({
              type: 'TOGGLE_DISPLAY_SUPPORT_MODAL',
            });
          }
          // (SSO_V2) Direct Unauthenticated user to the provided support link
          return window.open(
            'https://resources.trendmicro.com/Hybrid-Cloud-Security-CO-Contact-Us.html',
            '_blank',
            'noopener,noreferrer'
          );
        }
        // (SSO_V1) Show Old Unauthenticated Support form for unauthenticated user
        if (typeof window !== 'undefined') {
          const getIframe = window.top.document.getElementById('c1c-web-app-workload');
          const contentWin = getIframe ? getIframe.contentWindow : null;
          return contentWin ? contentWin.doSupport() : null;
        }
      }
    }
  };

  getAccountsMenuList = () => {
    const { t } = this.props;
    const { cloudOneAccounts = [] } = this.state;
    let accountMenuList = [];

    const checkAccountsState = (value) =>
      cloudOneAccounts.some((account) => account.value === value);

    if (checkAccountsState('noJoinedAccounts') || cloudOneAccounts.length < 1) {
      accountMenuList = [
        {
          label: t('accountsDropdown.noJoinedAccounts'),
          value: 'noJoinedAccounts',
          disabled: false,
        },
        { label: t('accountsDropdown.createAccount'), value: 'createAccount', disabled: false },
      ];
    } else if (checkAccountsState('noLastAccessedAccount')) {
      accountMenuList = [
        { label: t('accountsDropdown.accountSwitch'), value: 'accountSwitch', disabled: false },
        { label: t('accountsDropdown.createAccount'), value: 'createAccount', disabled: false },
      ];
    } else {
      accountMenuList = [
        { label: t('accountsDropdown.accountSwitch'), value: 'accountSwitch', disabled: false },
        { label: t('accountsDropdown.accountSettings'), value: 'manageAccount', disabled: false },
        { label: t('accountsDropdown.createAccount'), value: 'createAccount', disabled: false },
      ];
    }

    if (this.state.isSAMLUser) {
      accountMenuList = accountMenuList.filter(({ value }) => value !== 'createAccount');
    }

    return accountMenuList;
  };

  onMediaQueryChange = (mediaQueryList) => {
    if (mediaQueryList.matches) {
      const localeLinks =
        this.props.locale === 'ja'
          ? [
              {
                label: 'English',
                value: 'en',
                disabled: false,
              },
            ]
          : [
              {
                label: '日本語',
                value: 'ja',
                disabled: false,
              },
            ];
      // Only Update when the support links do not already include a locale link
      this.setState((prevState) => {
        if (this.state.supportLinks.length > 2) return;
        return {
          ...prevState,
          supportLinks: [...this.state.supportLinks, ...localeLinks],
        };
      });
    } else if (!mediaQueryList.matches && this.state.supportLinks?.length > 2) {
      this.setState((prevState) => ({
        ...prevState,
        supportLinks: this.supportLinks(),
      }));
    }
  };

  updateSupportLinks = () => {
    const mediaQueryList = window.matchMedia('(max-width: 640px)');
    this.setState(
      (prevState) => ({
        ...prevState,
        supportLinks: this.supportLinks(),
      }),
      () => this.onMediaQueryChange(mediaQueryList)
    );
  };

  getServicesList = (t, c1ServicesList, serviceRoleUrnList) => {
    const availableServices = [];
    // TODO: change id to 'central' once API response is updated
    if (!isGovCloudRegion && c1ServicesList.find((service) => service.id === 'projects')) {
      availableServices.push(
        {
          label: t('serviceDropdown.cloudOneCentral'),
          value: 'cloud-one-central',
          isNew: true,
          disabled: false,
        },
        {
          label: 'showMenuDivider',
        }
      );
    }

    c1ServicesList.forEach((service) => {
      // add available security services to the list except Cloud Sentry

      const isServiceMigrated =
        this.props.workloadMigrationState === 'migrated' && service.id === 'workload';

      const isServiceMigrating =
        this.props.workloadMigrationState === 'migrating' && service.id === 'workload';

      let serviceLink = `/${service.id}`;
      if (service.id === 'workload') {
        serviceLink =
          {
            migrated: getV1PortalURL(),
            migrating: '',
          }[this.props.workloadMigrationState] ?? serviceLink;
      }

      const popoverContent = isServiceMigrated && (
        <Text>
          {t('migratedServicesPopoverText.workload.description')}
          <Link
            href={t('migratedServicesPopoverText.workload.learnMore.link')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('migratedServicesPopoverText.workload.learnMore.text')}
          </Link>
        </Text>
      );

      const deprecatedSuffix = isServiceMigrated ? ` ${t('deprecated')}` : '';

      if (service.category === 'security' && isServiceEnabledForTargetEnv(service)) {
        const hasRole =
          serviceRoleUrnList?.some((serviceRoleUrn) => serviceRoleUrn.includes(service.id)) ?? true;
        const isServiceEnabled = (hasRole && !isServiceMigrating) || isServiceMigrated;
        availableServices.push({
          label: t(`serviceDropdown.${service.id}`) + `${deprecatedSuffix}`,
          value: service.id,
          href: serviceLink,
          disabled: !isServiceEnabled,
          popoverContent: popoverContent,
        });
      }
    });

    availableServices.push({
      label: 'showMenuDivider',
    });

    // add common services, always visible
    availableServices.push(
      {
        label: t('serviceDropdown.administration'),
        value: 'administration',
        disabled: false,
      },
      {
        label: t('serviceDropdown.billing'),
        value: 'billing',
      },
      {
        label: t('serviceDropdown.integrations'),
        value: 'integrations',
        disabled: false,
      },
      {
        label: t('serviceDropdown.myUser'),
        value: 'my-user',
        disabled: false,
      }
    );
    return availableServices;
  };

  render() {
    const {
      t,
      theme: { colorMode },
      globalEventDistributor,
      userDetails: { name, email },
      isLoading,
      c1ServicesList,
      serviceRoleUrnList,
      locale,
      userURN,
      c1Token,
    } = this.props;
    const { isLoggedIn, hasSSOv2, showDropDown, showBanner, value, selectedAccount } = this.state;

    const servicesList = this.getServicesList(t, c1ServicesList, serviceRoleUrnList);

    if (hasEnableMfeTemplate) {
      const mfeTemplate = {
        label: t('serviceDropdown.mfeTemplate'),
        value: 'mfe-template',
        disabled: false,
      };
      servicesList.push(mfeTemplate);
    }

    // Determine if the document matches the media query string then appened language toggle
    const isResponsive = !isLoggedIn && hasSSOv2;
    const mediaQueryList = window.matchMedia('(max-width: 640px)');

    // Update Support Links for Hamburger Menu
    if (isResponsive) {
      mediaQueryList.addEventListener('change', (_) => this.onMediaQueryChange(mediaQueryList));
    }

    const userAccounts = [
      { label: t('myProfile'), value: 'my-profile', disabled: false },
      { label: t('signOut'), value: 'sign-out', disabled: false },
    ];

    const selectedService = value
      ? servicesList.find((service) => service.value === value)
      : servicesList[0];

    const isWSservice = servicesList.some(
      ({ value, disabled }) => value === 'workload' && !disabled
    );

    // add wrapper around the loader based on the colorMode set in redux, header itself is always dark
    const ColorModeWrapper = colorMode === 'dark' ? DarkMode : LightMode;

    return (
      <>
        <Flex direction="row" align="center" height="40px" paddingY="0" paddingX="4x">
          <Wrapper data-id="header-nav">
            <Global
              styles={css`
                :root {
                  color-scheme: ${colorMode};
                  background-color: ${colorMode === 'dark'
                    ? '#212121'
                    : 'rgba(255, 255, 255, 1.0)'};
                }
              `}
            />
            <Flex
              align="center"
              tabIndex={0}
              onClick={this.onLogoClick}
              onKeyDown={(e) => {
                if (e?.key === 'Enter') this.onLogoClick();
              }}
              cursor="pointer"
              data-id="c1-logo"
            >
              <Image
                src={logo}
                id="trend-logo"
                data-id="trend-logo"
                height="24px"
                width="24px"
                marginRight="2x"
                alt="Trend Cloud One"
              />
              <Text size="xl" fontWeight="light" fontFamily="interstate" display="inline">
                {t('cloudOneHeader.title')}
                <Text as="sup" display="inline">
                  &trade;
                </Text>
              </Text>
              <Space width="2x" />
              {isLoggedIn && showDropDown && selectedService ? <Icon icon="chevron-right" /> : ''}
            </Flex>
          </Wrapper>

          {isLoggedIn && showDropDown && (
            <ServiceDropdownMenu
              id="c1c_service_dropdown"
              selectedItem={selectedService}
              menuList={servicesList}
              menuButtonProps={{ fontSize: 'lg' }}
              onChange={this.onServiceSelect}
            />
          )}

          <Stack direction="row" align="center" flex="auto" justifyContent="flex-end">
            {isLoggedIn &&
              hasSSOv2 &&
              !!userURN &&
              !!selectedAccount?.value &&
              !isGovCloudRegion &&
              isWSservice &&
              window.location.pathname !== '/account-selection' && (
                <NotificationsDropdownMenu
                  isResponsive={isResponsive}
                  currentAccount={{ ...selectedAccount, id: selectedAccount?.value }}
                  c1Token={c1Token}
                  locale={locale}
                  userURN={userURN}
                />
              )}
            {isLoggedIn && hasSSOv2 && selectedAccount && (
              <DropdownMenu
                id="c1c_accounts_dropdown"
                tabIndex={0}
                selectedItem={selectedAccount}
                menuList={this.getAccountsMenuList()}
                onChange={this.onAccountSelect}
              />
            )}

            {isLoggedIn && hasSSOv2 && (
              <DropdownMenu
                id="c1c_users_dropdown"
                tabIndex={0}
                truncateString={true}
                selectedItem={name || email}
                menuList={userAccounts}
                onChange={this.onUserSelect}
              />
            )}
            {isLoggedIn && !hasSSOv2 && (
              <Wrapper data-id="signout_link" cursor="pointer">
                <Text
                  tabIndex={0}
                  onClick={this.onLogout}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter') this.onLogout();
                  }}
                >
                  {t('signOut')}
                </Text>
                <Space width="2x" />
              </Wrapper>
            )}
            {!isLoggedIn && hasSSOv2 && !isGovCloudRegion && (
              <Flex display={{ sm: 'none', md: 'flex' }} align="center">
                <LangToggle globalEventDistributor={globalEventDistributor} />
              </Flex>
            )}
            <DropdownMenu
              id="c1c_support_dropdown"
              selectedItem={t('helpDropdown.title')}
              menuList={this.state.supportLinks}
              onChange={this.onSupportSelect}
              isResponsive={isResponsive}
            />
          </Stack>
        </Flex>
        {showBanner && <Banner t={t} />}
        {isLoading ? (
          <ColorModeWrapper>
            <GlobalLoader isLoading={true} />
          </ColorModeWrapper>
        ) : null}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  appSelection: getCommonType(state, 'appSelection'),
  isLoading: getCommonType(state, 'toggleGlobalLoader'),
  c1AccountsList: getAccountsList(state),
  authType: getCommonType(state, 'authType'),
  locale: getCommonType(state, 'locale'),
  loggedInSSOv2: getCommonType(state, 'loggedIn'),
  wsLoggedIn: getCommonType(state, 'wsLoggedIn'),
  theme: getCommonType(state, 'theme'),
  wsNavigationRestricted: getCommonType(state, 'wsNavigationRestricted'),
  userDetails: getUserDetails(state),
  c1ServicesList: getServices(state, 'c1ServicesList'),
  workloadMigrationState: getAccountServicesMigratedState(state, 'workload'),
  serviceRoleUrnList: getToken(state, 'serviceRoleUrnList'),
  c1Token: getToken(state, 'token'),
  userURN: getToken(state, 'userUrn'),
});

export default connect(mapStateToProps)(withTranslation()(App));
